import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import BrandLogo from "./BrandLogo";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/activities", label: "Activities" },
  { to: "/team", label: "Team" },
  { to: "/directory", label: "Directory" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  // 🔒 prevent background scroll when menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  return (
    <>
      {/* NAVBAR */}
      <header className="sticky top-0 z-50 bg-white/30 backdrop-blur-xl border-b border-black/10">
        <div className="container-px flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <BrandLogo compact imageClassName="w-10 h-10 md:w-11 md:h-11" />
            <span className="font-heading text-base sm:text-lg md:text-xl font-bold tracking-tight text-black leading-tight truncate pr-2">
              Oswal Sabha Professional Forum
            </span>
          </Link>

          {/* DESKTOP NAV */}
          <nav className="hidden md:flex items-center gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isActive
                    ? "bg-black text-white"
                    : "text-black/70 hover:text-black hover:bg-black/10"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* RIGHT SIDE */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/member/login"
              className="text-black/70 hover:text-black text-sm transition"
            >
              Apply / Sign in
            </Link>

            <button
              onClick={() => nav("/member/login?mode=register")}
              className="bg-brand text-white px-5 py-2 rounded-full text-sm hover:scale-105 transition"
            >
              Become a Member
            </button>
          </div>

          {/* MOBILE BUTTON */}
          <button
            className="md:hidden ml-2 shrink-0 p-2 text-black"
            onClick={() => setOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* MOBILE MENU */}
      <div
        className={`fixed inset-0 z-50 transition-all duration-300 ${open ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
      >
        {/* BACKDROP */}
        <div
          className="absolute inset-0 bg-black/25 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />

        {/* SLIDE PANEL */}
        <div
          className={`absolute top-0 right-0 w-[85%] max-w-sm h-full bg-white/30 backdrop-blur-2xl border-l border-white/35 shadow-2xl p-6 transform transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"
            }`}
        >
          {/* TOP */}
          <div className="flex justify-between items-center mb-8">
            <span className="text-ink text-lg font-semibold">Menu</span>
            <button
              onClick={() => setOpen(false)}
              className="rounded-full bg-white/45 p-2 text-ink shadow-sm border border-white/40"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* LINKS */}
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `rounded-2xl px-4 py-3 text-xl font-medium transition border ${isActive
                    ? "bg-white/70 text-brand border-white/70 shadow-sm"
                    : "bg-white/25 text-ink hover:bg-white/45 border-white/30"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          {/* ACTIONS */}
          <div className="mt-10 flex flex-col gap-4">
            <button
              onClick={() => {
                setOpen(false);
                nav("/member/login?mode=register");
              }}
              className="bg-brand/90 backdrop-blur text-white py-3 rounded-xl shadow-lg"
            >
              Become a Member
            </button>

            <button
              onClick={() => {
                setOpen(false);
                nav("/member/login");
              }}
              className="border border-white/45 bg-white/30 text-ink py-3 rounded-xl shadow-sm"
            >
              Apply / Sign in
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
